import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils";

export const theme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        colorError: {
          backgroundColor: "hsl(var(--error))",
        },
        contained: {
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
        },
        outlined: {
          borderColor: "hsl(var(--primary))",
          color: "hsl(var(--primary))",
          backgroundColor: "hsl(var(--background))",
        },
        text: {
          color: "hsl(var(--primary))",
          "&:hover": {
            backgroundColor: "hsl(var(--muted))",
          },
        },
        root: {
          "&.Mui-disabled": {
            backgroundColor: "hsl(var(--muted))",
            color: "hsl(var(--muted-foreground))",
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: "hsl(var(--border))",
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          color: "hsl(var(--foreground))",
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slots: {
          openPickerIcon: ({ ownerState, ...props }) => (
            <Calendar className={cn("size-4")} />
          ),
        },
      },
    },
    MuiDateTimePicker: {
      defaultProps: {
        slots: {
          openPickerIcon: ({ ownerState, ...props }) => (
            <Calendar className={cn("size-4")} />
          ),
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
          color: "hsl(var(--foreground))",
        },
        columnSeparator: {
          maxWidth: "2px",
        },
        toolbar: {
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
          color: "hsl(var(--foreground))",
        },
        overlay: {
          backgroundColor: "var(--card)",
        },
        columnHeader: {
          color: "hsl(var(--muted-foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        },

        footerContainer: {
          color: "hsl(var(--muted-foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        },
        cell: {
          color: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
          borderWidth: "1px",
          borderRadius: "var(--radius)",
          boxShadow: "none",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "hsl(var(--muted-foreground))",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: "hsl(var(--foreground))",
          borderColor: "hsl(var(--border))",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: "hsl(var(--border))",
          color: "hsl(var(--foreground))",
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          fill: "hsl(var(--foreground))",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "hsl(var(--muted-foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: "hsl(var(--muted-foreground))",
        },
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          fill: "hsl(var(--foreground))",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          color: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: "hsl(var(--border))",
          variants: [
            {
              props: { variant: "head" },
              style: {
                color: "hsl(var(--muted-foreground))",
                backgroundColor: "hsl(var(--card))",
              },
            },
            {
              props: { variant: "body" },
              style: {
                color: "hsl(var(--foreground))",
                backgroundColor: "hsl(var(--card))",
              },
            },
          ],
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: "hsl(var(--foreground))",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "hsl(var(--muted))",
          },
          "&:focus": {
            backgroundColor: "hsl(var(--muted))",
          },
          "&.MuiPickersDay-today:not(.Mui-selected)": {
            borderColor: "hsl(var(--primary))",
          },
          "&.MuiPickersDay-dayOutsideMonth": {
            color: "hsl(var(--muted-foreground))",
          },
          "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
            color: "hsl(var(--primary-foreground))",
            backgroundColor: "hsl(var(--primary))",
          },
          "&.Mui-disabled:not(.Mui-selected)": {
            color: "hsl(var(--muted-foreground))",
            opacity: 0.5,
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: "hsl(var(--muted-foreground))",
        },
        weekNumberLabel: {
          color: "hsl(var(--muted-foreground))",
        },
        weekNumber: {
          color: "hsl(var(--muted-foreground))",
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: "hsl(var(--foreground))",
        },
      },
    },
  },
});
